import { Tables } from '@/supabase/types'
import {
  BrandColorToken,
  BrandProfile,
  BrandGuidelineSnapshot,
  BrandLogoGuidelines,
  BrandProfileStatus,
  BrandSocialProductionGuidelines,
  BrandTypographySettings,
  ContentActivityEntry,
  ContentActivityType,
  ContentAssetPack,
  ContentBlogVariant,
  ContentBlogVariantMode,
  ContentCampaign,
  ContentCarouselPlan,
  ContentCarouselSlide,
  ContentExecutionDoc,
  ContentExecutionDocType,
  ContentGeneratedAssetMetadata,
  ContentOutputRecord,
  ContentPlatform,
  ContentSystemRecord,
  ContentWorkflowStatus,
  CONTENT_SYSTEM_FOLDER_TYPES,
  CONTENT_SYSTEM_PROMPT_TYPES
} from '@/lib/content-system/types'

const DEFAULT_PLATFORMS: ContentPlatform[] = [
  'blog',
  'linkedin',
  'x',
  'instagram',
  'video',
  'image'
]

function createEmptyBrandTypographySettings(): BrandTypographySettings {
  return {
    primaryFont: '',
    secondaryFont: '',
    accentFont: '',
    headingStyle: '',
    bodyStyle: ''
  }
}

function createEmptyBrandLogoGuidelines(): BrandLogoGuidelines {
  return {
    clearspace: '',
    minimumSize: '',
    backgroundRules: '',
    approvedLockups: [],
    dos: [],
    donts: []
  }
}

function createEmptyBrandSocialProduction(): BrandSocialProductionGuidelines {
  return {
    designPrinciples: [],
    approvedFormats: [],
    overlayRules: [],
    ctaTreatments: [],
    accessibilityRules: [],
    motionRules: [],
    imageStyleRules: [],
    prohibitedVisuals: []
  }
}

function createEmptyStrategy() {
  return {
    campaignTitle: '',
    bigIdea: '',
    coreNarrative: '',
    audienceHook: '',
    contentPillars: [],
    callsToAction: [],
    voiceReminders: []
  }
}

function createEmptyBlogPack() {
  return {
    title: '',
    metaDescription: '',
    outline: [],
    draft: '',
    variants: [],
    activeVariantId: null
  }
}

function createEmptySocialPack() {
  return {
    linkedin: [],
    x: [],
    instagramCaptions: [],
    instagramReelScript: '',
    newsletterSnippet: ''
  }
}

function createEmptyVisualPack() {
  return {
    imagePrompts: [],
    carouselPlans: [],
    videoStoryboard: [],
    videoPrompt: '',
    reelPrompt: '',
    reelStoryboard: [],
    thumbnailIdeas: [],
    generatedAssets: []
  }
}

export function createEmptyAssetPack(): ContentAssetPack {
  const now = new Date().toISOString()

  return {
    strategy: createEmptyStrategy(),
    calendar: [],
    blog: createEmptyBlogPack(),
    social: createEmptySocialPack(),
    visuals: createEmptyVisualPack(),
    repurposing: [],
    qualityChecklist: [],
    generatedAt: now,
    model: 'losi-omega'
  }
}

export function createActivityEntry(
  type: ContentActivityType,
  message: string,
  actorEmail: string | null = null
): ContentActivityEntry {
  const timestamp = new Date().toISOString()

  return {
    id: `activity_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    type,
    message,
    timestamp,
    actorEmail
  }
}

function createRecordId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

export function appendActivity<T extends { activityLog: ContentActivityEntry[] }>(
  record: T,
  entry: ContentActivityEntry
): T {
  return {
    ...record,
    activityLog: [...(record.activityLog || []), entry]
  }
}

export function createEmptyBrandProfile(): BrandProfile {
  const now = new Date().toISOString()

  return {
    kind: 'brand-profile',
    name: '',
    companyName: 'Marshmallow Studio',
    audience: '',
    offer: '',
    brandSummary: '',
    voice: '',
    pillars: [],
    dos: [],
    donts: [],
    bannedWords: [],
    preferredPhrases: [],
    visualDirection: '',
    brandColors: [],
    typography: createEmptyBrandTypographySettings(),
    logoGuidelines: createEmptyBrandLogoGuidelines(),
    socialProduction: createEmptyBrandSocialProduction(),
    websiteUrls: [],
    notes: '',
    guidelineMarkdown: '',
    guidelineIterations: [],
    activeGuidelineIterationId: null,
    brandGuidelineFileIds: [],
    inspirationFileIds: [],
    brandLogoFileIds: [],
    socialTemplateFileIds: [],
    status: 'active',
    archivedAt: null,
    lastUsedAt: null,
    activityLog: [],
    createdAt: now,
    updatedAt: now
  }
}

export function createEmptyCampaign(): ContentCampaign {
  const now = new Date().toISOString()

  return {
    kind: 'campaign',
    name: '',
    topic: '',
    primaryObjective: 'Drive awareness and qualified demand',
    audience: '',
    offer: '',
    keyMessage: '',
    callToAction: '',
    additionalContext: '',
    campaignWeeks: 4,
    postsPerWeek: 4,
    platforms: DEFAULT_PLATFORMS,
    brandProfileId: null,
    referenceFileIds: [],
    status: 'draft',
    outputs: null,
    outputRecordIds: [],
    lastGeneratedOutputId: null,
    reviewNotes: '',
    activityLog: [],
    createdAt: now,
    updatedAt: now
  }
}

export function createEmptyOutputRecord(): ContentOutputRecord {
  const now = new Date().toISOString()

  return {
    kind: 'asset-pack',
    name: '',
    campaignId: null,
    brandProfileId: null,
    sourceCampaignName: '',
    topic: '',
    status: 'generated',
    model: 'losi-omega',
    assetPack: createEmptyAssetPack(),
    executionDocIds: [],
    generatedAssetFileIds: [],
    wordpressDraftId: null,
    wordpressDraftUrl: null,
    activityLog: [],
    createdAt: now,
    updatedAt: now
  }
}

export function createExecutionDocRecord(): ContentExecutionDoc {
  const now = new Date().toISOString()

  return {
    kind: 'execution-doc',
    name: '',
    docType: 'campaign-runbook',
    campaignId: null,
    outputRecordId: null,
    brandProfileId: null,
    platform: null,
    content: '',
    createdAt: now,
    updatedAt: now
  }
}

function normalizeUnknownStringArray(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value.map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export function normalizeBrandColorTokens(value: unknown): BrandColorToken[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item, index) => {
      if (!isRecord(item)) {
        return null
      }

      const name = typeof item.name === 'string' ? item.name.trim() : ''
      const hex = typeof item.hex === 'string' ? item.hex.trim().toUpperCase() : ''
      const role = typeof item.role === 'string' ? item.role.trim() : ''
      const usage = typeof item.usage === 'string' ? item.usage.trim() : ''

      if (!name && !hex && !role && !usage) {
        return null
      }

      return {
        id: typeof item.id === 'string' ? item.id : createRecordId(`brand_color_${index + 1}`),
        name: name || `Color ${index + 1}`,
        hex,
        role,
        usage
      } satisfies BrandColorToken
    })
    .filter((item): item is BrandColorToken => Boolean(item))
}

export function normalizeBrandTypographySettings(value: unknown): BrandTypographySettings {
  const record = isRecord(value) ? value : {}

  return {
    ...createEmptyBrandTypographySettings(),
    primaryFont: typeof record.primaryFont === 'string' ? record.primaryFont.trim() : '',
    secondaryFont: typeof record.secondaryFont === 'string' ? record.secondaryFont.trim() : '',
    accentFont: typeof record.accentFont === 'string' ? record.accentFont.trim() : '',
    headingStyle: typeof record.headingStyle === 'string' ? record.headingStyle.trim() : '',
    bodyStyle: typeof record.bodyStyle === 'string' ? record.bodyStyle.trim() : ''
  }
}

export function normalizeBrandLogoGuidelines(value: unknown): BrandLogoGuidelines {
  const record = isRecord(value) ? value : {}

  return {
    ...createEmptyBrandLogoGuidelines(),
    clearspace: typeof record.clearspace === 'string' ? record.clearspace.trim() : '',
    minimumSize: typeof record.minimumSize === 'string' ? record.minimumSize.trim() : '',
    backgroundRules:
      typeof record.backgroundRules === 'string' ? record.backgroundRules.trim() : '',
    approvedLockups: normalizeUnknownStringArray(record.approvedLockups),
    dos: normalizeUnknownStringArray(record.dos),
    donts: normalizeUnknownStringArray(record.donts)
  }
}

export function normalizeBrandSocialProduction(value: unknown): BrandSocialProductionGuidelines {
  const record = isRecord(value) ? value : {}

  return {
    ...createEmptyBrandSocialProduction(),
    designPrinciples: normalizeUnknownStringArray(record.designPrinciples),
    approvedFormats: normalizeUnknownStringArray(record.approvedFormats),
    overlayRules: normalizeUnknownStringArray(record.overlayRules),
    ctaTreatments: normalizeUnknownStringArray(record.ctaTreatments),
    accessibilityRules: normalizeUnknownStringArray(record.accessibilityRules),
    motionRules: normalizeUnknownStringArray(record.motionRules),
    imageStyleRules: normalizeUnknownStringArray(record.imageStyleRules),
    prohibitedVisuals: normalizeUnknownStringArray(record.prohibitedVisuals)
  }
}

export function createContentBlogVariant(
  blog: Pick<ContentAssetPack['blog'], 'title' | 'metaDescription' | 'outline' | 'draft'>,
  options?: {
    id?: string
    label?: string
    mode?: ContentBlogVariantMode
    request?: string
    summary?: string
    createdAt?: string
  }
): ContentBlogVariant {
  const createdAt = options?.createdAt || new Date().toISOString()

  return {
    id: options?.id || createRecordId('blog_variant'),
    label: options?.label || 'V1',
    mode: options?.mode || 'generated',
    request: options?.request || 'Initial content-system blog draft.',
    summary: options?.summary || 'Initial blog draft saved from the asset pack.',
    title: blog.title || '',
    metaDescription: blog.metaDescription || '',
    outline: Array.isArray(blog.outline) ? blog.outline.filter(Boolean) : [],
    draft: blog.draft || '',
    createdAt
  }
}

function normalizeBlogVariants(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item, index) => {
      if (!isRecord(item)) {
        return null
      }

      return createContentBlogVariant(
        {
          title: typeof item.title === 'string' ? item.title : '',
          metaDescription: typeof item.metaDescription === 'string' ? item.metaDescription : '',
          outline: normalizeUnknownStringArray(item.outline),
          draft: typeof item.draft === 'string' ? item.draft : ''
        },
        {
          id: typeof item.id === 'string' ? item.id : undefined,
          label: typeof item.label === 'string' ? item.label : `V${index + 1}`,
          mode: item.mode === 'iterated' ? 'iterated' : 'generated',
          request: typeof item.request === 'string' ? item.request : '',
          summary: typeof item.summary === 'string' ? item.summary : '',
          createdAt: typeof item.createdAt === 'string' ? item.createdAt : undefined
        }
      )
    })
    .filter((item): item is ContentBlogVariant => Boolean(item))
}

export function normalizeContentBlogPack(value: unknown) {
  const emptyBlogPack = createEmptyBlogPack()
  const record = isRecord(value) ? value : {}

  const baseBlog = {
    ...emptyBlogPack,
    title: typeof record.title === 'string' ? record.title : emptyBlogPack.title,
    metaDescription:
      typeof record.metaDescription === 'string'
        ? record.metaDescription
        : emptyBlogPack.metaDescription,
    outline: normalizeUnknownStringArray(record.outline),
    draft: typeof record.draft === 'string' ? record.draft : emptyBlogPack.draft
  }

  const variants = normalizeBlogVariants(record.variants)
  const seededVariants =
    variants.length > 0
      ? variants
      : baseBlog.title || baseBlog.metaDescription || baseBlog.outline.length || baseBlog.draft
        ? [
            createContentBlogVariant(baseBlog, {
              label: 'V1',
              mode: 'generated'
            })
          ]
        : []

  const activeVariantId =
    typeof record.activeVariantId === 'string' &&
    seededVariants.some((variant) => variant.id === record.activeVariantId)
      ? record.activeVariantId
      : seededVariants[seededVariants.length - 1]?.id || null

  const activeVariant = seededVariants.find((variant) => variant.id === activeVariantId) || null

  if (!activeVariant) {
    return {
      ...baseBlog,
      variants: seededVariants,
      activeVariantId
    }
  }

  return {
    ...baseBlog,
    title: activeVariant.title,
    metaDescription: activeVariant.metaDescription,
    outline: activeVariant.outline,
    draft: activeVariant.draft,
    variants: seededVariants,
    activeVariantId
  }
}

export function applyContentBlogVariant(
  blog: ContentAssetPack['blog'],
  variant: ContentBlogVariant
) {
  const variants = [...(blog.variants || [])]
  const index = variants.findIndex((item) => item.id === variant.id)

  if (index === -1) {
    variants.push(variant)
  } else {
    variants[index] = variant
  }

  return normalizeContentBlogPack({
    ...blog,
    title: variant.title,
    metaDescription: variant.metaDescription,
    outline: variant.outline,
    draft: variant.draft,
    variants,
    activeVariantId: variant.id
  })
}

function normalizeCarouselSlides(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item, index) => {
      if (!isRecord(item)) {
        return null
      }

      const title = typeof item.title === 'string' ? item.title.trim() : ''
      const prompt = typeof item.prompt === 'string' ? item.prompt.trim() : ''

      if (!title && !prompt) {
        return null
      }

      return {
        id: typeof item.id === 'string' ? item.id : createRecordId(`carousel_slide_${index + 1}`),
        title: title || `Slide ${index + 1}`,
        prompt
      } satisfies ContentCarouselSlide
    })
    .filter((item): item is ContentCarouselSlide => Boolean(item))
}

function normalizeCarouselPlans(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item, index) => {
      if (!isRecord(item)) {
        return null
      }

      const slides = normalizeCarouselSlides(item.slides)
      if (!slides.length) {
        return null
      }

      return {
        id: typeof item.id === 'string' ? item.id : createRecordId(`carousel_${index + 1}`),
        title:
          typeof item.title === 'string' && item.title.trim()
            ? item.title.trim()
            : `Carousel ${index + 1}`,
        platform:
          typeof item.platform === 'string' && item.platform.trim()
            ? item.platform.trim()
            : 'instagram',
        slides
      } satisfies ContentCarouselPlan
    })
    .filter((item): item is ContentCarouselPlan => Boolean(item))
}

function normalizeGeneratedAssets(value: unknown) {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => {
      if (!isRecord(item)) {
        return null
      }

      if (typeof item.fileId !== 'string' || !item.fileId.trim()) {
        return null
      }

      return {
        fileId: item.fileId,
        kind:
          item.kind === 'carousel-slide' ||
          item.kind === 'video' ||
          item.kind === 'reel' ||
          item.kind === 'edited-image'
            ? item.kind
            : 'image',
        label: typeof item.label === 'string' ? item.label : '',
        createdAt: typeof item.createdAt === 'string' ? item.createdAt : new Date().toISOString(),
        providerJobId: typeof item.providerJobId === 'string' ? item.providerJobId : null,
        collectionId: typeof item.collectionId === 'string' ? item.collectionId : null,
        collectionLabel: typeof item.collectionLabel === 'string' ? item.collectionLabel : null,
        sequenceIndex:
          typeof item.sequenceIndex === 'number' && Number.isFinite(item.sequenceIndex)
            ? item.sequenceIndex
            : null,
        sequenceTotal:
          typeof item.sequenceTotal === 'number' && Number.isFinite(item.sequenceTotal)
            ? item.sequenceTotal
            : null,
        useAsGenerationReference:
          typeof item.useAsGenerationReference === 'boolean'
            ? item.useAsGenerationReference
            : item.kind !== 'video' && item.kind !== 'reel'
      } satisfies ContentGeneratedAssetMetadata
    })
    .filter((item): item is ContentGeneratedAssetMetadata => Boolean(item))
}

export function normalizeContentVisualPack(
  value: unknown,
  social?: Partial<ContentAssetPack['social']>
) {
  const emptyVisualPack = createEmptyVisualPack()
  const record = isRecord(value) ? value : {}
  const reelPrompt =
    typeof record.reelPrompt === 'string' && record.reelPrompt.trim()
      ? record.reelPrompt.trim()
      : typeof social?.instagramReelScript === 'string'
        ? social.instagramReelScript.trim()
        : ''

  return {
    ...emptyVisualPack,
    imagePrompts: normalizeUnknownStringArray(record.imagePrompts),
    carouselPlans: normalizeCarouselPlans(record.carouselPlans),
    videoStoryboard: normalizeUnknownStringArray(record.videoStoryboard),
    videoPrompt: typeof record.videoPrompt === 'string' ? record.videoPrompt : '',
    reelPrompt,
    reelStoryboard: normalizeUnknownStringArray(record.reelStoryboard),
    thumbnailIdeas: normalizeUnknownStringArray(record.thumbnailIdeas),
    generatedAssets: normalizeGeneratedAssets(record.generatedAssets)
  }
}

export function normalizeListInput(value: string): string[] {
  return value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean)
}

export function brandColorsToTextarea(value: BrandColorToken[]): string {
  return value
    .map((color) =>
      [color.name.trim(), color.hex.trim(), color.role.trim(), color.usage.trim()]
        .filter(Boolean)
        .join(' | ')
    )
    .filter(Boolean)
    .join('\n')
}

export function normalizeBrandColorTextarea(value: string): BrandColorToken[] {
  return value
    .split('\n')
    .map((line, index) => {
      const trimmed = line.trim()
      if (!trimmed) {
        return null
      }

      const [name = '', hex = '', role = '', usage = ''] = trimmed
        .split('|')
        .map((part) => part.trim())

      return {
        id: createRecordId(`brand_color_${index + 1}`),
        name: name || `Color ${index + 1}`,
        hex: hex.toUpperCase(),
        role,
        usage
      } satisfies BrandColorToken
    })
    .filter((item): item is BrandColorToken => Boolean(item))
}

export function listToTextarea(value: string[]): string {
  return value.join('\n')
}

export function urlListToTextarea(value: string[]): string {
  return value.join('\n')
}

function renderMarkdownList(items: string[], emptyLabel = 'None specified yet.'): string {
  if (!items.length) {
    return `- ${emptyLabel}`
  }

  return items.map((item) => `- ${item}`).join('\n')
}

export function buildBrandGuidelineMarkdown(snapshot: BrandGuidelineSnapshot): string {
  const brandName = snapshot.name || snapshot.companyName || 'Untitled Brand'
  const colorTokens = snapshot.brandColors.length
    ? snapshot.brandColors
        .map((color) => {
          const fragments = [color.name]

          if (color.hex) {
            fragments.push(`(${color.hex})`)
          }

          const detail = [color.role, color.usage].filter(Boolean).join(' — ')
          return `- ${fragments.join(' ')}${detail ? ` — ${detail}` : ''}`
        })
        .join('\n')
    : '- No brand colors defined yet.'

  return `# ${brandName} Brand Guideline

## Positioning

${snapshot.brandSummary || 'Positioning still needs to be defined.'}

## Audience

${snapshot.audience || 'Audience still needs to be defined.'}

## Offer

${snapshot.offer || 'Offer still needs to be defined.'}

## Voice And Tone

${snapshot.voice || 'Voice guidance still needs to be defined.'}

## Content Pillars

${renderMarkdownList(snapshot.pillars)}

## Preferred Phrases

${renderMarkdownList(snapshot.preferredPhrases)}

## Banned Words

${renderMarkdownList(snapshot.bannedWords)}

## Do

${renderMarkdownList(snapshot.dos)}

## Don't

${renderMarkdownList(snapshot.donts)}

## Visual Direction

${snapshot.visualDirection || 'Visual direction still needs to be defined.'}

## Brand Colors

${colorTokens}

## Typography

- Primary font: ${snapshot.typography.primaryFont || 'Not defined yet.'}
- Secondary font: ${snapshot.typography.secondaryFont || 'Not defined yet.'}
- Accent font: ${snapshot.typography.accentFont || 'Not defined yet.'}
- Heading style: ${snapshot.typography.headingStyle || 'Not defined yet.'}
- Body style: ${snapshot.typography.bodyStyle || 'Not defined yet.'}

## Logo Usage

- Clearspace: ${snapshot.logoGuidelines.clearspace || 'Not defined yet.'}
- Minimum size: ${snapshot.logoGuidelines.minimumSize || 'Not defined yet.'}
- Background rules: ${snapshot.logoGuidelines.backgroundRules || 'Not defined yet.'}

### Approved Lockups

${renderMarkdownList(snapshot.logoGuidelines.approvedLockups)}

### Logo Do

${renderMarkdownList(snapshot.logoGuidelines.dos)}

### Logo Don't

${renderMarkdownList(snapshot.logoGuidelines.donts)}

## Social Production System

### Design Principles

${renderMarkdownList(snapshot.socialProduction.designPrinciples)}

### Approved Formats

${renderMarkdownList(snapshot.socialProduction.approvedFormats)}

### Overlay Rules

${renderMarkdownList(snapshot.socialProduction.overlayRules)}

### CTA Treatments

${renderMarkdownList(snapshot.socialProduction.ctaTreatments)}

### Accessibility Rules

${renderMarkdownList(snapshot.socialProduction.accessibilityRules)}

### Motion Rules

${renderMarkdownList(snapshot.socialProduction.motionRules)}

### Image Style Rules

${renderMarkdownList(snapshot.socialProduction.imageStyleRules)}

### Prohibited Visuals

${renderMarkdownList(snapshot.socialProduction.prohibitedVisuals)}

## Website And Source URLs

${renderMarkdownList(snapshot.websiteUrls)}

## Extra Notes

${snapshot.notes || 'No extra notes yet.'}
`
}

export function createBrandGuidelineSnapshot(
  brandProfile: Pick<
    BrandProfile,
    | 'name'
    | 'companyName'
    | 'audience'
    | 'offer'
    | 'brandSummary'
    | 'voice'
    | 'pillars'
    | 'dos'
    | 'donts'
    | 'bannedWords'
    | 'preferredPhrases'
    | 'visualDirection'
    | 'brandColors'
    | 'typography'
    | 'logoGuidelines'
    | 'socialProduction'
    | 'websiteUrls'
    | 'notes'
    | 'guidelineMarkdown'
  >
): BrandGuidelineSnapshot {
  const snapshot: BrandGuidelineSnapshot = {
    name: brandProfile.name || '',
    companyName: brandProfile.companyName || '',
    audience: brandProfile.audience || '',
    offer: brandProfile.offer || '',
    brandSummary: brandProfile.brandSummary || '',
    voice: brandProfile.voice || '',
    pillars: [...(brandProfile.pillars || [])],
    dos: [...(brandProfile.dos || [])],
    donts: [...(brandProfile.donts || [])],
    bannedWords: [...(brandProfile.bannedWords || [])],
    preferredPhrases: [...(brandProfile.preferredPhrases || [])],
    visualDirection: brandProfile.visualDirection || '',
    brandColors: normalizeBrandColorTokens(brandProfile.brandColors),
    typography: normalizeBrandTypographySettings(brandProfile.typography),
    logoGuidelines: normalizeBrandLogoGuidelines(brandProfile.logoGuidelines),
    socialProduction: normalizeBrandSocialProduction(brandProfile.socialProduction),
    websiteUrls: [...(brandProfile.websiteUrls || [])],
    notes: brandProfile.notes || '',
    guidelineMarkdown: brandProfile.guidelineMarkdown || ''
  }

  return {
    ...snapshot,
    guidelineMarkdown: snapshot.guidelineMarkdown.trim() || buildBrandGuidelineMarkdown(snapshot)
  }
}

export function applyBrandGuidelineSnapshot(
  brandProfile: BrandProfile,
  snapshot: BrandGuidelineSnapshot,
  options?: {
    activeGuidelineIterationId?: string | null
  }
): BrandProfile {
  const normalizedSnapshot = createBrandGuidelineSnapshot(snapshot)

  return {
    ...brandProfile,
    name: normalizedSnapshot.name,
    companyName: normalizedSnapshot.companyName,
    audience: normalizedSnapshot.audience,
    offer: normalizedSnapshot.offer,
    brandSummary: normalizedSnapshot.brandSummary,
    voice: normalizedSnapshot.voice,
    pillars: [...normalizedSnapshot.pillars],
    dos: [...normalizedSnapshot.dos],
    donts: [...normalizedSnapshot.donts],
    bannedWords: [...normalizedSnapshot.bannedWords],
    preferredPhrases: [...normalizedSnapshot.preferredPhrases],
    visualDirection: normalizedSnapshot.visualDirection,
    brandColors: normalizeBrandColorTokens(normalizedSnapshot.brandColors),
    typography: normalizeBrandTypographySettings(normalizedSnapshot.typography),
    logoGuidelines: normalizeBrandLogoGuidelines(normalizedSnapshot.logoGuidelines),
    socialProduction: normalizeBrandSocialProduction(normalizedSnapshot.socialProduction),
    websiteUrls: [...normalizedSnapshot.websiteUrls],
    notes: normalizedSnapshot.notes,
    guidelineMarkdown: normalizedSnapshot.guidelineMarkdown,
    activeGuidelineIterationId:
      options?.activeGuidelineIterationId === undefined
        ? brandProfile.activeGuidelineIterationId
        : options.activeGuidelineIterationId
  }
}

export function safeJsonParse<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

function collectTextFragments(value: unknown, fragments: string[]) {
  if (typeof value === 'string') {
    if (value) {
      fragments.push(value)
    }
    return
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectTextFragments(item, fragments))
    return
  }

  if (!value || typeof value !== 'object') {
    return
  }

  const record = value as Record<string, unknown>

  if (typeof record.json === 'string') {
    fragments.push(record.json)
  } else if (record.json && typeof record.json === 'object') {
    fragments.push(JSON.stringify(record.json))
  }

  if (typeof record.text === 'string') {
    fragments.push(record.text)
  } else if (record.text) {
    collectTextFragments(record.text, fragments)
  }

  if (typeof record.output_text === 'string') {
    fragments.push(record.output_text)
  }

  if (typeof record.value === 'string') {
    fragments.push(record.value)
  }

  if (typeof record.arguments === 'string') {
    fragments.push(record.arguments)
  }

  if (typeof record.content === 'string') {
    fragments.push(record.content)
  } else if (record.content) {
    collectTextFragments(record.content, fragments)
  }

  if (Array.isArray(record.parts)) {
    collectTextFragments(record.parts, fragments)
  }

  if (Array.isArray(record.summary)) {
    collectTextFragments(record.summary, fragments)
  }
}

export function extractMessageTextContent(value: unknown): string {
  const fragments: string[] = []
  collectTextFragments(value, fragments)

  return fragments
    .map((fragment) => fragment.trim())
    .filter(Boolean)
    .join('\n')
}

export function extractCompletionTextContent(value: unknown): string {
  if (!value || typeof value !== 'object') {
    return extractMessageTextContent(value)
  }

  const record = value as Record<string, unknown>
  const choices = Array.isArray(record.choices) ? record.choices : []
  const firstChoice =
    choices[0] && typeof choices[0] === 'object' ? (choices[0] as Record<string, unknown>) : null
  const firstMessage =
    firstChoice?.message && typeof firstChoice.message === 'object'
      ? (firstChoice.message as Record<string, unknown>)
      : firstChoice?.message
  const firstMessageContent =
    firstMessage && typeof firstMessage === 'object'
      ? (firstMessage as Record<string, unknown>).content
      : firstMessage

  const candidates = [
    extractMessageTextContent(firstMessageContent),
    extractMessageTextContent(firstMessage),
    typeof record.output_text === 'string' ? record.output_text.trim() : '',
    extractMessageTextContent(record.output),
    extractMessageTextContent(record.content)
  ]

  return candidates.find((candidate) => candidate.trim().length > 0) || ''
}

function findBalancedJsonSegment(value: string, openChar: '{' | '[', closeChar: '}' | ']') {
  let startIndex = value.indexOf(openChar)

  while (startIndex !== -1) {
    let depth = 0
    let inString = false
    let isEscaped = false

    for (let index = startIndex; index < value.length; index++) {
      const character = value[index]

      if (inString) {
        if (isEscaped) {
          isEscaped = false
          continue
        }

        if (character === '\\') {
          isEscaped = true
          continue
        }

        if (character === '"') {
          inString = false
        }

        continue
      }

      if (character === '"') {
        inString = true
        continue
      }

      if (character === openChar) {
        depth += 1
        continue
      }

      if (character === closeChar) {
        depth -= 1

        if (depth === 0) {
          return value.slice(startIndex, index + 1).trim()
        }
      }
    }

    startIndex = value.indexOf(openChar, startIndex + 1)
  }

  return ''
}

export function extractJsonBlock(value: string): string {
  const fencedMatch = value.match(/```json\s*([\s\S]*?)```/i)
  if (fencedMatch?.[1]) {
    return fencedMatch[1].trim()
  }

  const trimmedValue = value.trim()

  if (!trimmedValue) {
    return ''
  }

  const balancedObject = findBalancedJsonSegment(trimmedValue, '{', '}')
  if (balancedObject) {
    return balancedObject
  }

  const balancedArray = findBalancedJsonSegment(trimmedValue, '[', ']')
  if (balancedArray) {
    return balancedArray
  }

  return trimmedValue
}

export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export function formatStatusLabel(value: ContentWorkflowStatus | BrandProfileStatus): string {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function getExecutionDocTypeLabel(docType: ContentExecutionDocType): string {
  switch (docType) {
    case 'campaign-runbook':
      return 'Campaign Runbook'
    case 'blog-draft':
      return 'Blog Draft'
    case 'social-pack':
      return 'Social Pack'
    case 'visual-pack':
      return 'Visual Pack'
    case 'newsletter-pack':
      return 'Newsletter Pack'
    default:
      return docType
  }
}

export function makeDuplicateName(name: string) {
  return `${name.trim() || 'Untitled'} Copy`
}

export function serializeRecord(record: ContentSystemRecord): string {
  return JSON.stringify(record, null, 2)
}

export function parseContentSystemPrompt(prompt: Tables<'prompts'>): ContentSystemRecord | null {
  const parsed = safeJsonParse<ContentSystemRecord | null>(prompt.content, null)

  if (!parsed || typeof parsed !== 'object' || !('kind' in parsed)) {
    return null
  }

  if (parsed.kind === 'brand-profile') {
    return {
      ...createEmptyBrandProfile(),
      ...parsed,
      id: prompt.id,
      kind: 'brand-profile'
    }
  }

  if (parsed.kind === 'campaign') {
    return {
      ...createEmptyCampaign(),
      ...parsed,
      id: prompt.id,
      kind: 'campaign'
    }
  }

  if (parsed.kind === 'asset-pack') {
    return {
      ...createEmptyOutputRecord(),
      ...parsed,
      assetPack: {
        ...createEmptyAssetPack(),
        ...(parsed.assetPack || {})
      },
      id: prompt.id,
      kind: 'asset-pack'
    }
  }

  if (parsed.kind === 'execution-doc') {
    return {
      ...createExecutionDocRecord(),
      ...parsed,
      id: prompt.id,
      kind: 'execution-doc'
    }
  }

  return null
}

export function getFolderTypeForRecord(kind: ContentSystemRecord['kind']) {
  switch (kind) {
    case 'brand-profile':
      return CONTENT_SYSTEM_FOLDER_TYPES.brands
    case 'campaign':
      return CONTENT_SYSTEM_FOLDER_TYPES.campaigns
    case 'asset-pack':
    case 'execution-doc':
      return CONTENT_SYSTEM_FOLDER_TYPES.outputs
    default:
      return CONTENT_SYSTEM_FOLDER_TYPES.campaigns
  }
}

export function getPromptDescriptionForRecord(kind: ContentSystemRecord['kind']) {
  switch (kind) {
    case 'brand-profile':
      return CONTENT_SYSTEM_PROMPT_TYPES.brand
    case 'campaign':
      return CONTENT_SYSTEM_PROMPT_TYPES.campaign
    case 'asset-pack':
      return CONTENT_SYSTEM_PROMPT_TYPES.output
    case 'execution-doc':
      return CONTENT_SYSTEM_PROMPT_TYPES.executionDoc
    default:
      return CONTENT_SYSTEM_PROMPT_TYPES.campaign
  }
}

function buildCalendarMarkdown(assetPack: ContentAssetPack): string {
  return assetPack.calendar
    .map((week) => {
      const assets = week.assets
        .map(
          (asset) =>
            `- ${asset.day}: [${asset.platform}] ${asset.title} - ${asset.hook}\n  Format: ${asset.format}\n  Summary: ${asset.summary}\n  CTA: ${asset.cta}`
        )
        .join('\n')

      return `## Week ${week.week}: ${week.theme}\n\nGoal: ${week.goal}\n\n${assets}`
    })
    .join('\n\n')
}

function buildBlogVariantsMarkdown(assetPack: ContentAssetPack) {
  const variants =
    assetPack.blog.variants.length > 0
      ? assetPack.blog.variants
      : [
          createContentBlogVariant(assetPack.blog, {
            label: 'V1',
            mode: 'generated',
            summary: 'Primary blog draft.'
          })
        ]

  return variants
    .map(
      (variant) => `### ${variant.label}: ${variant.title}

Mode: ${variant.mode === 'iterated' ? 'Iterated' : 'Generated'}

Summary: ${variant.summary}

Meta description: ${variant.metaDescription}

#### Outline

${variant.outline.map((item) => `- ${item}`).join('\n')}

#### Draft

${variant.draft}`
    )
    .join('\n\n')
}

function buildCarouselPlansMarkdown(assetPack: ContentAssetPack) {
  if (!assetPack.visuals.carouselPlans.length) {
    return 'No carousel plans generated.'
  }

  return assetPack.visuals.carouselPlans
    .map(
      (plan) => `### ${plan.title} (${plan.platform})

${plan.slides
  .map(
    (slide, index) => `- Slide ${index + 1}: ${slide.title}
  Prompt: ${slide.prompt}`
  )
  .join('\n')}`
    )
    .join('\n\n')
}

export function buildCampaignMarkdown(campaign: ContentCampaign): string {
  const outputs = campaign.outputs
  if (!outputs) {
    return `# ${campaign.name || campaign.topic}\n\nNo generated asset pack yet.`
  }

  const activeBlogVariant =
    outputs.blog.variants.find((variant) => variant.id === outputs.blog.activeVariantId) || null

  return `# ${campaign.name || campaign.topic}

Status: ${formatStatusLabel(campaign.status)}

Topic: ${campaign.topic}

Primary objective: ${campaign.primaryObjective}

Audience: ${campaign.audience}

Offer: ${campaign.offer}

Key message: ${campaign.keyMessage}

Call to action: ${campaign.callToAction}

Additional context: ${campaign.additionalContext}

## Strategy

- Campaign title: ${outputs.strategy.campaignTitle}
- Big idea: ${outputs.strategy.bigIdea}
- Core narrative: ${outputs.strategy.coreNarrative}
- Audience hook: ${outputs.strategy.audienceHook}

## Content Pillars

${outputs.strategy.contentPillars.map((item) => `- ${item}`).join('\n')}

## Weekly Calendar

${buildCalendarMarkdown(outputs)}

## Blog Variants

Active variant: ${activeBlogVariant?.label || 'None selected'}

${buildBlogVariantsMarkdown(outputs)}

## LinkedIn

${outputs.social.linkedin.map((item, index) => `### Post ${index + 1}\n\n${item}`).join('\n\n')}

## X

${outputs.social.x.map((item, index) => `### Post ${index + 1}\n\n${item}`).join('\n\n')}

## Instagram

${outputs.social.instagramCaptions.map((item, index) => `### Caption ${index + 1}\n\n${item}`).join('\n\n')}

## Newsletter

${outputs.social.newsletterSnippet}

## Visuals

### Video Prompt

${outputs.visuals.videoPrompt}

### Reel Prompt

${outputs.visuals.reelPrompt}

### Image Prompts

${outputs.visuals.imagePrompts.map((item) => `- ${item}`).join('\n')}

### Carousel Plans

${buildCarouselPlansMarkdown(outputs)}

### Quality Checklist

${outputs.qualityChecklist.map((item) => `- ${item}`).join('\n')}
`
}

export function buildOutputRecordMarkdown(outputRecord: ContentOutputRecord): string {
  const { assetPack } = outputRecord

  return `# ${outputRecord.name}

Status: ${formatStatusLabel(outputRecord.status)}

Campaign: ${outputRecord.sourceCampaignName}

Topic: ${outputRecord.topic}

Generated: ${assetPack.generatedAt}

Model: ${outputRecord.model}

## Strategy

- Campaign title: ${assetPack.strategy.campaignTitle}
- Big idea: ${assetPack.strategy.bigIdea}
- Core narrative: ${assetPack.strategy.coreNarrative}
- Audience hook: ${assetPack.strategy.audienceHook}

## Calls To Action

${assetPack.strategy.callsToAction.map((item) => `- ${item}`).join('\n')}

## Weekly Calendar

${buildCalendarMarkdown(assetPack)}

## Repurposing

${assetPack.repurposing.map((item) => `- ${item}`).join('\n')}

## Quality Checklist

${assetPack.qualityChecklist.map((item) => `- ${item}`).join('\n')}
`
}

export function buildBlogExecutionMarkdown(outputRecord: ContentOutputRecord): string {
  const { assetPack } = outputRecord
  const activeBlogVariant =
    assetPack.blog.variants.find((variant) => variant.id === assetPack.blog.activeVariantId) || null

  return `# ${assetPack.blog.title}

Campaign: ${outputRecord.sourceCampaignName}

Topic: ${outputRecord.topic}

Active variant: ${activeBlogVariant?.label || 'None selected'}

Meta description: ${assetPack.blog.metaDescription}

## Outline

${assetPack.blog.outline.map((item) => `- ${item}`).join('\n')}

## Draft

${assetPack.blog.draft}

## All Blog Variants

${buildBlogVariantsMarkdown(assetPack)}
`
}

export function buildSocialExecutionMarkdown(outputRecord: ContentOutputRecord): string {
  const { assetPack } = outputRecord

  return `# ${outputRecord.sourceCampaignName} Social Pack

Topic: ${outputRecord.topic}

## LinkedIn

${assetPack.social.linkedin.map((item, index) => `### Post ${index + 1}\n\n${item}`).join('\n\n')}

## X

${assetPack.social.x.map((item, index) => `### Post ${index + 1}\n\n${item}`).join('\n\n')}

## Instagram Captions

${assetPack.social.instagramCaptions.map((item, index) => `### Caption ${index + 1}\n\n${item}`).join('\n\n')}

## Instagram Reel Script

${assetPack.social.instagramReelScript}
`
}

export function buildVisualExecutionMarkdown(outputRecord: ContentOutputRecord): string {
  const { assetPack } = outputRecord

  return `# ${outputRecord.sourceCampaignName} Visual Pack

Topic: ${outputRecord.topic}

## Image Prompts

${assetPack.visuals.imagePrompts.map((item, index) => `### Prompt ${index + 1}\n\n${item}`).join('\n\n')}

## Video Prompt

${assetPack.visuals.videoPrompt}

## Video Storyboard

${assetPack.visuals.videoStoryboard.map((item) => `- ${item}`).join('\n')}

## Reel Prompt

${assetPack.visuals.reelPrompt}

## Reel Storyboard

${assetPack.visuals.reelStoryboard.map((item) => `- ${item}`).join('\n')}

## Carousel Plans

${buildCarouselPlansMarkdown(assetPack)}

## Thumbnail Ideas

${assetPack.visuals.thumbnailIdeas.map((item) => `- ${item}`).join('\n')}
`
}

export function buildNewsletterExecutionMarkdown(outputRecord: ContentOutputRecord): string {
  const { assetPack } = outputRecord

  return `# ${outputRecord.sourceCampaignName} Newsletter Pack

Topic: ${outputRecord.topic}

## Newsletter Snippet

${assetPack.social.newsletterSnippet}

## Repurposing Ideas

${assetPack.repurposing.map((item) => `- ${item}`).join('\n')}
`
}

export function buildExecutionDocContent(
  outputRecord: ContentOutputRecord,
  docType: ContentExecutionDocType
) {
  switch (docType) {
    case 'campaign-runbook':
      return buildOutputRecordMarkdown(outputRecord)
    case 'blog-draft':
      return buildBlogExecutionMarkdown(outputRecord)
    case 'social-pack':
      return buildSocialExecutionMarkdown(outputRecord)
    case 'visual-pack':
      return buildVisualExecutionMarkdown(outputRecord)
    case 'newsletter-pack':
      return buildNewsletterExecutionMarkdown(outputRecord)
    default:
      return buildOutputRecordMarkdown(outputRecord)
  }
}

export function stripMarkdownForExcerpt(value: string, limit = 180) {
  const cleaned = value
    .replace(/^#+\s+/gm, '')
    .replace(/^[-*]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/\n+/g, ' ')
    .trim()

  if (cleaned.length <= limit) {
    return cleaned
  }

  return `${cleaned.slice(0, limit).trim()}...`
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function convertTextToBasicHtml(value: string) {
  return value
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const lines = block
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)

      if (lines.length && lines.every((line) => /^[-*]\s+/.test(line))) {
        return `<ul>${lines
          .map((line) => `<li>${escapeHtml(line.replace(/^[-*]\s+/, ''))}</li>`)
          .join('')}</ul>`
      }

      if (lines.length && lines.every((line) => /^\d+\.\s+/.test(line))) {
        return `<ol>${lines
          .map((line) => `<li>${escapeHtml(line.replace(/^\d+\.\s+/, ''))}</li>`)
          .join('')}</ol>`
      }

      if (block.startsWith('### ')) {
        return `<h3>${escapeHtml(block.slice(4))}</h3>`
      }

      if (block.startsWith('## ')) {
        return `<h2>${escapeHtml(block.slice(3))}</h2>`
      }

      if (block.startsWith('# ')) {
        return `<h1>${escapeHtml(block.slice(2))}</h1>`
      }

      return `<p>${escapeHtml(block).replace(/\n/g, '<br />')}</p>`
    })
    .join('\n')
}
